import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';

const SEARCH_SECTION_ID = 'predictive-search-header';

/**
 * A custom element that manages the header's single dropdown panel, shared
 * between the menu and search triggers. Only one of the two can be open at
 * a time; opening one while the other is open swaps the panel's content
 * instead of closing and reopening it.
 *
 * Live product results are fetched via the Section Rendering API against
 * `sections/predictive-search-header.liquid`, which reuses Shopify's native
 * `predictive_search` object — no hand-rolled /search/suggest.json calls.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} menuToggle
 * @property {HTMLButtonElement} searchToggle
 * @property {HTMLElement} menuPanel
 * @property {HTMLElement} searchPanel
 * @property {HTMLInputElement} [searchInput]
 * @property {HTMLButtonElement} [clearButton]
 * @property {HTMLElement} [searchResults]
 *
 * @extends {Component<Refs>}
 */
class HeaderPanel extends Component {
  requiredRefs = ['menuToggle', 'searchToggle', 'menuPanel', 'searchPanel'];

  /** @type {AbortController | null} */
  #searchAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('pointerdown', this.#onOutsideClick, true);
    this.#searchAbortController?.abort();
  }

  /** @returns {'menu' | 'search' | null} */
  get activePanel() {
    return /** @type {'menu' | 'search' | null} */ (this.getAttribute('active-panel'));
  }

  toggleMenu() {
    this.activePanel === 'menu' ? this.closePanel() : this.openPanel('menu');
  }

  toggleSearch() {
    this.activePanel === 'search' ? this.closePanel() : this.openPanel('search');
  }

  /**
   * @param {'menu' | 'search'} name
   */
  openPanel(name) {
    const wasOpen = Boolean(this.activePanel);

    this.setAttribute('active-panel', name);
    this.classList.add('header-panel--open');

    this.refs.menuPanel.hidden = name !== 'menu';
    this.refs.searchPanel.hidden = name !== 'search';

    this.refs.menuToggle.setAttribute('aria-expanded', String(name === 'menu'));
    this.refs.searchToggle.setAttribute('aria-expanded', String(name === 'search'));

    if (name === 'search') {
      requestAnimationFrame(() => this.refs.searchInput?.focus());
    }

    if (!wasOpen) {
      document.addEventListener('pointerdown', this.#onOutsideClick, true);
    }
  }

  closePanel() {
    if (!this.activePanel) return;

    this.removeAttribute('active-panel');
    this.classList.remove('header-panel--open');
    this.refs.menuToggle.setAttribute('aria-expanded', 'false');
    this.refs.searchToggle.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.#onOutsideClick, true);
  }

  clearSearch() {
    const input = this.refs.searchInput;
    if (!input) return;

    input.value = '';
    input.focus();
    this.#syncClearButton();
    this.#clearResults();
  }

  /**
   * Debounced handler wired to the search input's `on:input`. Fetches live
   * product results via the Section Rendering API and morphs them in.
   * @param {InputEvent} event
   */
  search = debounce((event) => {
    if (!event.inputType) return;

    this.#syncClearButton();

    const term = this.refs.searchInput?.value.trim() ?? '';

    if (!term.length) {
      this.#clearResults();
      return;
    }

    this.#fetchResults(term);
  }, 200);

  /**
   * @param {string} term
   */
  async #fetchResults(term) {
    const { searchResults } = this.refs;
    if (!searchResults) return;

    this.#searchAbortController?.abort();
    const controller = new AbortController();
    this.#searchAbortController = controller;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '5');
    url.searchParams.set('resources[limit_scope]', 'each');

    try {
      const html = await sectionRenderer.getSectionHTML(SEARCH_SECTION_ID, false, url, controller.signal);
      if (controller.signal.aborted) return;
      morph(searchResults, html);
    } catch (error) {
      if (controller.signal.aborted) return;
      console.warn('[header-panel] predictive search fetch failed:', error);
    }
  }

  #clearResults() {
    this.#searchAbortController?.abort();
    if (this.refs.searchResults) this.refs.searchResults.replaceChildren();
  }

  #syncClearButton() {
    if (!this.refs.searchInput || !this.refs.clearButton) return;
    this.refs.clearButton.hidden = this.refs.searchInput.value.length === 0;
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.activePanel) return;

    const panel = this.activePanel;
    this.closePanel();
    (panel === 'search' ? this.refs.searchToggle : this.refs.menuToggle)?.focus();
  };

  /**
   * @param {PointerEvent} event
   */
  #onOutsideClick = (event) => {
    if (!this.activePanel) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.closePanel();
  };
}

if (!customElements.get('header-panel-component')) {
  customElements.define('header-panel-component', HeaderPanel);
}
